'use client';

import { useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import gsap from 'gsap';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { CustomImage } from '..';
import styles from './BgBlurredTextRingPro.module.css';

const cx = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(' ');

// Tablet used the same asset as desktop, so two variants cover all breakpoints
const BACKGROUND_DESKTOP = 'web_v2/ring-pro/buy-v2/lifestyle/web.png/w_1920';
const BACKGROUND_MOBILE = 'web_v2/ring-pro/buy-v2/lifestyle/mob.png/w_1080';

const HeroTitle = ({ className }: { className?: string }) => {
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const container = useRef<HTMLDivElement>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const t = useTranslations('ring-pro');

  const line1 = t('ring-pro.bgBlurredSection.contentA1');
  const line2 = t('ring-pro.bgBlurredSection.contentA2');

  const animationInit = () => {
    if (!container.current || !window) return;
    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 0.5,
          ease: 'power2.easeInOut',
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top center ',
          end: '50px',
          scrub: 0.2,
          immediateRender: false,
        },
      });
      tl.fromTo(
        'h3 > span',
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.35,
          stagger: 0.05,
        },
        0.2,
      );
      tl.set('h3 > span', { autoAlpha: 1 });

      add(tl, 'ring-pro-bg-blurred-content1');
    });
  };

  useIsomorphicLayoutEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          animationInit();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    if (container.current) {
      intersectionObserver.current.observe(container.current);
    }

    return () => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
    };
  }, []);

  return (
    <div ref={container} className={cx(styles.heroTitle, className)}>
      <h3>
        {line1
          .split(' ')
          .map((text, index) => (
            <span key={`line1-${index}`}>{text} </span>
          ))}
        <br />
        {line2
          .split(' ')
          .map((text, index) => (
            <span key={`line2-${index}`}>{text} </span>
          ))}
      </h3>
    </div>
  );
};

const Content2RingPro = ({ className }: { className?: string }) => {
  const t = useTranslations('ring-pro');
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const container = useRef<HTMLDivElement>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  const configs = [
    {
      title: t('ring-pro.bgBlurredSection.contentB.a'),
      description: t('ring-pro.bgBlurredSection.contentB.a_desc'),
    },
    {
      title: t('ring-pro.bgBlurredSection.contentB.b'),
      description: t('ring-pro.bgBlurredSection.contentB.b_desc'),
    },
    {
      title: t('ring-pro.bgBlurredSection.contentB.c'),
      description: t('ring-pro.bgBlurredSection.contentB.c_desc'),
    },
  ];

  const animationInit = () => {
    if (!container.current || !window) return;
    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 0.5,
          ease: 'power2.easeInOut',
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top center ',
          end: '50px',
          scrub: 0.2,
          immediateRender: false,
        },
      });
      tl.fromTo(
        '.config-contents',
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.35, stagger: 0.06 },
        0.2,
      );
      add(tl, 'ring-pro-bg-blurred-content2');
    });
  };

  useIsomorphicLayoutEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) animationInit();
      },
      { root: null, threshold: 0 },
    );
    if (container.current) intersectionObserver.current.observe(container.current);
    return () => intersectionObserver.current?.disconnect();
  }, []);

  return (
    <div ref={container} className={cx(styles.content2, className)}>
      <div className={`config-contents ${styles.configContents}`}>
        {configs.map((config, index) => (
          <div key={`config-${index}`} className={styles.config}>
            <h3>{config.title}</h3>
            <p>{config.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const BgBlurredTextRingPro = ({
  className,
  htmlId,
}: {
  className?: string;
  htmlId?: string;
}) => {
  return (
    <div className={cx(styles.root, className)} id={htmlId}>
      <CustomImage
        src={BACKGROUND_DESKTOP}
        alt=""
        fill
        className={`${styles.imgContainer} ${styles.imgDesktop}`}
      />
      <CustomImage
        src={BACKGROUND_MOBILE}
        alt=""
        fill
        className={`${styles.imgContainer} ${styles.imgMobile}`}
      />
      <div className={styles.backdrop} />
      <div className={`${styles.content} ${styles.contentOne}`}>
        <HeroTitle />
      </div>
      <div className={`${styles.content} ${styles.contentTwo}`}>
        <Content2RingPro />
      </div>
    </div>
  );
};
